// SPDX-License-Identifier: Elastic-2.0
/**
 * useSuggestions — TanStack Query hooks for pending AI suggestions (T-022).
 *
 * Surfaces:
 *  - `useSuggestions()` — list query, polls every 60s (functional spec § 4.3).
 *  - `useTopPendingSuggestion()` — derived selector returning the single
 *    highest-confidence pending suggestion (or `null` when none exist).
 *    Used by the dashboard `<SuggestionStrip />` (T-022).
 *  - `usePendingSuggestionsCount()` — derived count consumed by the
 *    top-bar bell badge (T-020). Returns `0` while data is loading so
 *    the badge stays hidden until we have a real count.
 *  - `useApproveSuggestion()` / `useDismissSuggestion()` — mutations that
 *    invalidate `["suggestions"]` on success so the UI re-renders with
 *    the next pending suggestion.
 *
 * Status-filter note: the generated `Suggestion` model doesn't expose a
 * `status` field — the gateway already filters server-side
 * (`GET /suggestions` returns "every pending AI suggestion the user can
 * approve"), so every item in the response IS pending. We still re-export
 * the filter step (`status === "pending"`) defensively in case a future
 * API revision broadens the envelope; the cast keeps TS strict happy
 * without a model regen.
 *
 * Why we don't pass a per-call `queryClient`: the global one from
 * `@/api/queryClient` is mounted by `<QueryClientProvider>` in `App.tsx`
 * (Group 5 setup) — `useQueryClient()` picks it up via context.
 */
import { useMemo } from "react";
import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from "@tanstack/react-query";

import { SuggestionsService } from "@/api/gateway/services/SuggestionsService";
import type { Suggestion } from "@/api/gateway/models/Suggestion";
import type { SuggestionApproveResponse } from "@/api/gateway/models/SuggestionApproveResponse";
import type { SuggestionListResponse } from "@/api/gateway/models/SuggestionListResponse";

/** Shared TanStack query key — exported so tests + WS invalidation agree. */
export const SUGGESTIONS_QUERY_KEY = ["suggestions"] as const;

/**
 * Defensive `status` peek: the generated `Suggestion` type doesn't carry a
 * `status` discriminator at MVP, but the gateway envelope may add one
 * later. When absent we treat the suggestion as pending (which matches
 * the current `GET /suggestions` contract — it only returns pending rows).
 */
function isPending(suggestion: Suggestion): boolean {
  const status = (suggestion as Suggestion & { status?: string }).status;
  return status === undefined || status === "pending";
}

/**
 * Poll-based query for pending suggestions.
 *
 * Per functional spec § 4.3:
 *  - 60s staleTime + refetchInterval (poll cadence).
 *  - WS push (`suggestion_new` from T-023) invalidates this key
 *    out-of-band so users see new suggestions instantly without waiting
 *    for the next poll tick.
 */
export function useSuggestions(): UseQueryResult<SuggestionListResponse> {
  return useQuery({
    queryKey: SUGGESTIONS_QUERY_KEY,
    queryFn: () => SuggestionsService.listSuggestionsSuggestionsGet(),
    staleTime: 60_000,
    refetchInterval: 60_000,
  });
}

/**
 * Highest-confidence pending suggestion, or `null` if none / not loaded.
 *
 * Memoised so the sort doesn't run on every render of the dashboard.
 * Stable identity matters because consumers may pass the returned object
 * to memoised children (e.g. `<SuggestionCard suggestion={top} />`).
 */
export function useTopPendingSuggestion(): Suggestion | null {
  const { data } = useSuggestions();
  return useMemo(() => {
    if (!data?.suggestions || data.suggestions.length === 0) return null;
    const pending = data.suggestions.filter(isPending);
    if (pending.length === 0) return null;
    // Highest confidence first; stable across re-renders thanks to useMemo.
    return [...pending].sort((a, b) => b.confidence - a.confidence)[0] ?? null;
  }, [data]);
}

/**
 * Pending-suggestion count for the top-bar bell badge (T-020).
 *
 * Returns `0` while loading so the badge stays hidden during the initial
 * fetch — matches the "no badge until we know there's something" UX.
 */
export function usePendingSuggestionsCount(): number {
  const { data } = useSuggestions();
  return useMemo(() => {
    if (!data?.suggestions) return 0;
    return data.suggestions.filter(isPending).length;
  }, [data]);
}

/**
 * Approve a suggestion (materialises it as a routine).
 *
 * The dashboard `<SuggestionStrip />` does NOT call this directly — the
 * approval modal (T-025) owns the confirmation step. We expose the
 * mutation here so both the modal and the standalone suggestions page
 * (T-027) share the same invalidation + success/error semantics.
 */
export function useApproveSuggestion(): UseMutationResult<
  SuggestionApproveResponse,
  Error,
  string
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (suggestionId: string) =>
      SuggestionsService.approveSuggestionSuggestionsSuggestionIdApprovePost({
        suggestionId,
      }),
    onSuccess: () => {
      // Strip refreshes with the next-best pending suggestion; the new
      // routine surfaces in `useRoutines()` (separate key, separate hook).
      void queryClient.invalidateQueries({ queryKey: SUGGESTIONS_QUERY_KEY });
      void queryClient.invalidateQueries({ queryKey: ["routines"] });
      // eslint-disable-next-line no-console
      console.debug("[suggestions] approved");
    },
    onError: (error) => {
      // Telemetry-light at v0.1 — real endpoint TBD. Toast surface lives
      // in the caller so error copy can be context-specific.
      // eslint-disable-next-line no-console
      console.debug("[suggestions] approve failed", error);
    },
  });
}

/**
 * Dismiss a suggestion (without creating a routine).
 *
 * Used directly by `<SuggestionStrip />`'s "Not now" button — no modal.
 * On success the query invalidates and the strip re-renders with the
 * next-best pending suggestion (or hides if the list is empty).
 */
export function useDismissSuggestion(): UseMutationResult<void, Error, string> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (suggestionId: string) =>
      SuggestionsService.dismissSuggestionSuggestionsSuggestionIdDismissPost({
        suggestionId,
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: SUGGESTIONS_QUERY_KEY });
      // eslint-disable-next-line no-console
      console.debug("[suggestions] dismissed");
    },
    onError: (error) => {
      // eslint-disable-next-line no-console
      console.debug("[suggestions] dismiss failed", error);
    },
  });
}

/**
 * Confidence-to-i18n-key mapping (functional spec § 4.3).
 *
 * The strip's eyebrow renders "Mirlai noticed · {confidence label}".
 * Three buckets matches the design system's high/medium/low pill copy.
 *
 *  - `>= 0.8` → "suggestions.confidence.high"
 *  - `>= 0.5` → "suggestions.confidence.medium"
 *  - else     → "suggestions.confidence.low"
 *
 * Exported so the standalone Suggestions page (T-027) reuses the same
 * thresholds — keeping copy consistent across surfaces.
 */
export function confidenceLabelKey(confidence: number): string {
  if (confidence >= 0.8) return "suggestions.confidence.high";
  if (confidence >= 0.5) return "suggestions.confidence.medium";
  return "suggestions.confidence.low";
}
